import React, { useMemo } from 'react';
import { getCoreRowModel, getPaginationRowModel, useReactTable, type PaginationState } from '@tanstack/react-table';
import { Button, Group, Text, Modal, TextInput, ActionIcon, Tooltip } from '@mantine/core';
import { IconPlus, IconColumnInsertRight, IconFileDownload, IconCloudUpload, IconRefresh } from '@tabler/icons-react';
import Tabla from '../../../globales/componentes/tabla/tabla';
import { obtenerColumnasMoiAscensor, type MoiAscensorTableMeta } from './constantesTablaMoiAscensores';
import type { IConfiguracionMoiAscensor } from '../interfaces';

interface TablaMoiAscensoresVistaProps {
  datos: IConfiguracionMoiAscensor[];
  alDescargarPlantilla?: () => void;
  alCargaMasiva?: () => void;
  /** Vuelve a consultar la matriz (descarta una edición en curso). */
  alRecargar?: () => void;
  capacidades: string[];
  actualizarFila: (id: string, campo: string, valor: string | number) => void;
  eliminarFila: (id: string) => void;
  guardarMatriz?: () => void;
  guardarFila?: (id: string) => void;
  filaEnEdicion?: string | null;
  iniciarEdicion?: (id: string) => void;
  cancelarEdicion?: () => void;
  
  agregarFila: () => void;
  agregarCapacidad: (capacidad: string) => void;
  eliminarCapacidad: (capacidad: string) => void;
  editarCapacidad?: (antigua: string, nueva: string) => void;
  mostrarNotificacionObligatorio?: () => void;
  intentoGuardar?: boolean;
}

export const TablaMoiAscensoresVista: React.FC<TablaMoiAscensoresVistaProps> = ({
  datos,
  alDescargarPlantilla,
  alCargaMasiva,
  alRecargar,
  capacidades,
  actualizarFila,
  eliminarFila,
  guardarMatriz,
  guardarFila,
  filaEnEdicion,
  iniciarEdicion,
  cancelarEdicion,
  agregarFila,
  agregarCapacidad,
  eliminarCapacidad,
  editarCapacidad,
  mostrarNotificacionObligatorio,
  intentoGuardar
}) => {
  const [modalAbierto, setModalAbierto] = React.useState(false);
  const [nuevaCapacidad, setNuevaCapacidad] = React.useState('');
  const [capacidadAEditar, setCapacidadAEditar] = React.useState<string | null>(null);
  const columnas = useMemo(() => obtenerColumnasMoiAscensor(capacidades), [capacidades]);

  const meta: MoiAscensorTableMeta = {
    actualizarFila,
    eliminarFila,
    guardarMatriz,
    guardarFila,
    filaEnEdicion,
    iniciarEdicion,
    cancelarEdicion,
    eliminarCapacidad,
    iniciarEdicionCapacidad: (cap: string) => {
      setCapacidadAEditar(cap);
      setNuevaCapacidad(cap);
      setModalAbierto(true);
    },
    mostrarNotificacionObligatorio,
    intentoGuardar
  };

  // Paginación en el front: la matriz se guarda completa (guardado masivo), así que se carga
  // entera y solo se pagina la vista.
  const [paginacion, setPaginacion] = React.useState<PaginationState>({ pageIndex: 0, pageSize: 15 });

  const tabla = useReactTable({
    data: datos,
    columns: columnas,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: row => row.Id,
    meta,
    state: { pagination: paginacion },
    onPaginationChange: setPaginacion,
    autoResetPageIndex: false
  });

  // Si se eliminan filas y la página actual queda fuera de rango, ir a la última que exista.
  React.useEffect(() => {
    const ultimaPagina = Math.max(0, Math.ceil(datos.length / paginacion.pageSize) - 1);
    if (paginacion.pageIndex > ultimaPagina) setPaginacion(p => ({ ...p, pageIndex: ultimaPagina }));
  }, [datos.length, paginacion.pageIndex, paginacion.pageSize]);

  // La fila nueva se agrega de primera: ir a la primera página para que quede visible.
  const agregarFilaVisible = () => {
    setPaginacion(p => ({ ...p, pageIndex: 0 }));
    agregarFila();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Group justify="space-between" mb="sm">
        <Text fw={600} size="sm" c="dimmed">
          Tabla Matriz de Mano de Obra Directa de Ascensores
        </Text>
        <Group gap="sm">
          {/* Íconos con el estilo estándar de las tablas de los demás módulos (Tareas, Equipos, Clientes). */}
          <Group gap={4}>
            {alRecargar && (
              <Tooltip zIndex={100000} label="Refrescar" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
                <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alRecargar}>
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip zIndex={100000} label="Descargar Plantilla" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
              <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alDescargarPlantilla} disabled={!alDescargarPlantilla}>
                <IconFileDownload size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip zIndex={100000} label="Carga Masiva" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
              <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alCargaMasiva} disabled={!alCargaMasiva}>
                <IconCloudUpload size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Button
            variant="filled"
            color="primary" 
            size="xs" 
            radius="xl"
            leftSection={<IconColumnInsertRight size={14} />}
            onClick={() => setModalAbierto(true)}
          >
            Añadir Capacidad (Columna)
          </Button>
          <Button 
            variant="light" 
            color="primary" 
            size="xs" 
            radius="xl"
            leftSection={<IconPlus size={14} />}
            onClick={agregarFilaVisible}
            disabled={filaEnEdicion !== null && filaEnEdicion !== undefined}
          >
            Añadir Parada (Fila)
          </Button>
        </Group>
      </Group>

      <div style={{ flex: 1, minHeight: 400 }}>
        <Tabla tabla={tabla} modoEdicion={true} filaEnEdicion={filaEnEdicion} />
      </div>

      <Modal opened={modalAbierto} onClose={() => { setModalAbierto(false); setCapacidadAEditar(null); setNuevaCapacidad(''); }} title={capacidadAEditar ? "Editar Capacidad" : "Añadir Nueva Capacidad"} centered>
        <TextInput
          label="Capacidad"
          placeholder="Ej. 2500"
          rightSection={<Text size="xs" c="dimmed">kg</Text>}
          value={nuevaCapacidad}
          onChange={(e) => setNuevaCapacidad(e.currentTarget.value)}
          data-autofocus
          size="xs"
          radius="md"
          withAsterisk
          styles={{ label: { fontWeight: 500, marginBottom: '5px' } }}
        />
        <Group justify="flex-end" mt="xl">
          <Button variant="light" color="primary" size="xs" radius="xl" onClick={() => { setModalAbierto(false); setCapacidadAEditar(null); setNuevaCapacidad(''); }}>Cancelar</Button>
          <Button variant="filled" color="primary" size="xs" radius="xl" onClick={() => {
            if (nuevaCapacidad.trim()) {
              if (capacidadAEditar && editarCapacidad) {
                editarCapacidad(capacidadAEditar, nuevaCapacidad.trim());
              } else {
                agregarCapacidad(nuevaCapacidad.trim());
              }
              setNuevaCapacidad('');
              setCapacidadAEditar(null);
              setModalAbierto(false);
            }
          }}>
            Guardar
          </Button>
        </Group>
      </Modal>
    </div>
  );
};
